import { useTranslation } from 'react-i18next';
import useLazyImage from '@/hooks/useLazyImage';

const WhyChooseUniqueCode = () => {
  const { t } = useTranslation();
  const [imgRef, imgSrc, imgLoaded] = useLazyImage(
    'https://public.readdy.ai/ai/img_res/f3cc5f59b63c1f7bc45e996d0adfc906.jpg'
  );

  const features = [
    { icon: 'ri-shield-check-line', titleKey: 'whyUC.f1Title', descKey: 'whyUC.f1Desc' },
    { icon: 'ri-customer-service-2-line', titleKey: 'whyUC.f2Title', descKey: 'whyUC.f2Desc' },
    { icon: 'ri-time-line', titleKey: 'whyUC.f3Title', descKey: 'whyUC.f3Desc' },
    { icon: 'ri-global-line', titleKey: 'whyUC.f4Title', descKey: 'whyUC.f4Desc' },
    { icon: 'ri-team-line', titleKey: 'whyUC.f5Title', descKey: 'whyUC.f5Desc' },
    { icon: 'ri-award-line', titleKey: 'whyUC.f6Title', descKey: 'whyUC.f6Desc' },
  ];

  return (
    <section className="py-24 bg-gray-950 text-white relative overflow-hidden">
      <div className="absolute top-0 right-0 w-1/2 h-full pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-l from-brand-red/10 via-transparent to-transparent"></div>
      </div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-brand-red/5 rounded-full blur-3xl pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left — Image with lazy load */}
          <div className="relative order-2 lg:order-1">
            <div className="relative rounded-3xl overflow-hidden w-full h-80 bg-gray-800">
              <img
                ref={imgRef}
                src={imgSrc}
                alt="Unique Code Team"
                width={800}
                height={600}
                decoding="async"
                className={`w-full h-80 object-cover object-top transition-opacity duration-500 ${imgSrc ? 'opacity-100' : 'opacity-0'}`}
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-black/60 via-transparent to-transparent"></div>
            </div>

            <div className="absolute -bottom-6 -right-4 rtl:-right-auto rtl:-left-4 bg-white text-gray-900 rounded-2xl p-5 shadow-2xl min-w-52">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 flex items-center justify-center bg-brand-red rounded-xl">
                  <i className="ri-trophy-line text-white text-lg"></i>
                </div>
                <div>
                  <div className="font-black text-2xl text-gray-900">98%</div>
                  <div className="text-gray-500 text-xs">{t('whyUC.satisfactionLabel')}</div>
                </div>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-brand-red rounded-full" style={{ width: '98%' }}></div>
              </div>
            </div>

            <div className="absolute -top-4 -left-4 rtl:-left-auto rtl:-right-4 bg-brand-red text-white rounded-2xl p-4 shadow-xl">
              <div className="font-black text-3xl">5★</div>
              <div className="text-red-100 text-xs mt-0.5">{t('whyUC.ratingLabel')}</div>
            </div>
          </div>

          {/* Right — Content */}
          <div className="order-1 lg:order-2">
            <p className="text-brand-red font-semibold text-sm uppercase tracking-widest mb-3">{t('whyUC.label')}</p>
            <h2 className="text-4xl md:text-5xl font-black text-white leading-tight mb-6">
              {t('whyUC.title1')}
              <span className="block text-brand-red">{t('whyUC.title2')}</span>
            </h2>
            <p className="text-gray-400 text-base leading-relaxed mb-10">
              {t('whyUC.desc')}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {features.map((f) => (
                <div key={f.titleKey} className="flex items-start gap-4 p-4 bg-white/5 hover:bg-white/10 rounded-xl border border-white/10 hover:border-brand-red/30 transition-all duration-300 cursor-default group">
                  <div className="w-9 h-9 flex items-center justify-center bg-brand-red/20 rounded-lg flex-shrink-0 group-hover:bg-brand-red transition-colors duration-300">
                    <i className={`${f.icon} text-brand-red group-hover:text-white text-base transition-colors duration-300`}></i>
                  </div>
                  <div>
                    <div className="font-bold text-white text-sm mb-0.5">{t(f.titleKey)}</div>
                    <div className="text-gray-500 text-xs leading-relaxed">{t(f.descKey)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUniqueCode;
